import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Phone, Users, ArrowRight, Volume2, VolumeX, 
  LogOut, CheckCircle, XCircle, HelpCircle, Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Prize ladder values
const PRIZE_LADDER = [
  100, 200, 300, 500, 1000,
  2000, 4000, 8000, 16000, 32000,
  64000, 125000, 250000, 500000, 1000000
];

const SAFETY_CHECKPOINTS = [5, 10];

export default function GamePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  // Game State
  const [gameData, setGameData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [lifelines, setLifelines] = useState({
    fifty_fifty: true,
    ask_audience: true,
    phone_friend: true
  });
  const [guaranteedPrize, setGuaranteedPrize] = useState(0);
  
  // UI State
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerState, setAnswerState] = useState(null); // null, 'selected', 'correct', 'wrong'
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [disabledOptions, setDisabledOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Modal State
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [audiencePoll, setAudiencePoll] = useState(null);
  const [phoneFriend, setPhoneFriend] = useState(null);

  // Fetch game session
  const fetchGameSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/game/${sessionId}`);
      
      if (response.data.status !== "active") {
        navigate(`/results/${sessionId}`);
        return;
      }
      
      setGameData(response.data);
      setCurrentQuestion(response.data.question);
      setQuestionNumber(response.data.question_number);
      setLifelines(response.data.lifelines);
      setGuaranteedPrize(response.data.guaranteed_prize);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching game:", error);
      toast.error("حدث خطأ في تحميل اللعبة");
      navigate("/");
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    fetchGameSession();
  }, [fetchGameSession]);

  // Handle answer selection
  const handleSelectAnswer = (option) => {
    if (answerState || isSubmitting) return;
    setSelectedAnswer(option);
    setAnswerState('selected');
  };

  // Confirm answer
  const handleConfirmAnswer = async () => {
    if (!selectedAnswer || isSubmitting) return;
    
    setIsSubmitting(true);
    setShowConfirmModal(false);
    
    try {
      const response = await axios.post(`${API}/game/${sessionId}/answer`, {
        question_id: currentQuestion.id,
        selected_option: selectedAnswer
      });
      
      setCorrectAnswer(response.data.correct_option);
      
      if (response.data.is_correct) {
        setAnswerState('correct');
        toast.success("إجابة صحيحة! 🎉");
        
        // Wait and load next question or go to results
        setTimeout(() => {
          if (response.data.status === "won") {
            navigate(`/results/${sessionId}`);
          } else {
            setCurrentQuestion(response.data.next_question);
            setQuestionNumber(response.data.question_number);
            setGuaranteedPrize(response.data.guaranteed_prize);
            setSelectedAnswer(null);
            setAnswerState(null);
            setCorrectAnswer(null);
            setDisabledOptions([]);
          }
        }, 2000);
      } else {
        setAnswerState('wrong');
        toast.error(`إجابة خاطئة! الإجابة الصحيحة: ${response.data.correct_option}`);
        
        setTimeout(() => {
          navigate(`/results/${sessionId}`);
        }, 3000);
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
      toast.error("حدث خطأ في إرسال الإجابة");
      setAnswerState(null);
      setSelectedAnswer(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 50:50 Lifeline
  const useFiftyFifty = async () => {
    if (!lifelines.fifty_fifty) return;
    
    try {
      const response = await axios.post(`${API}/game/${sessionId}/lifeline/fifty-fifty`);
      
      const allOptions = ['A', 'B', 'C', 'D'];
      const toDisable = allOptions.filter(opt => !response.data.remaining_options.includes(opt));
      
      setDisabledOptions(toDisable);
      setLifelines(response.data.lifelines);
      toast.success("تم استخدام 50:50");
    } catch (error) {
      console.error("Error using 50:50:", error);
      toast.error("حدث خطأ في استخدام المساعدة");
    }
  };

  // Ask Audience Lifeline
  const useAskAudience = async () => {
    if (!lifelines.ask_audience) return;
    
    try {
      const response = await axios.post(`${API}/game/${sessionId}/lifeline/ask-audience`);
      
      setAudiencePoll(response.data.poll_results);
      setLifelines(response.data.lifelines);
      setShowAudienceModal(true);
    } catch (error) {
      console.error("Error using audience:", error);
      toast.error("حدث خطأ في استخدام المساعدة");
    }
  };

  // Phone Friend Lifeline
  const usePhoneFriend = async () => {
    if (!lifelines.phone_friend) return;
    
    try {
      const response = await axios.post(`${API}/game/${sessionId}/lifeline/phone-friend`);
      
      setPhoneFriend(response.data);
      setLifelines(response.data.lifelines);
      setShowPhoneModal(true);
    } catch (error) {
      console.error("Error using phone:", error);
      toast.error("حدث خطأ في استخدام المساعدة");
    }
  };

  // Quit Game
  const handleQuit = async () => {
    try {
      await axios.post(`${API}/game/${sessionId}/quit`);
      navigate(`/results/${sessionId}`);
    } catch (error) {
      console.error("Error quitting:", error);
      toast.error("حدث خطأ");
    }
  };

  const getOptionClass = (option) => {
    let baseClass = "answer-hexagon";
    
    if (disabledOptions.includes(option)) {
      return `${baseClass} disabled-option`;
    }
    
    if (answerState === 'correct' && option === correctAnswer) {
      return `${baseClass} correct`;
    }
    
    if (answerState === 'wrong') {
      if (option === selectedAnswer) return `${baseClass} wrong`;
      if (option === correctAnswer) return `${baseClass} correct`;
    }
    
    if (selectedAnswer === option) {
      return `${baseClass} selected`;
    }
    
    return baseClass;
  };

  const getOptionText = (option) => {
    if (!currentQuestion) return "";
    const optionMap = {
      'A': currentQuestion.option_a,
      'B': currentQuestion.option_b,
      'C': currentQuestion.option_c,
      'D': currentQuestion.option_d
    };
    return optionMap[option] || "";
  };

  if (isLoading) {
    return (
      <div className="studio-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-16 h-16 mx-auto mb-4"></div>
          <p className="text-white font-body text-xl">جاري تحميل اللعبة...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="studio-background min-h-screen flex flex-col lg:flex-row">
      {/* Prize Ladder - Desktop */}
      <div className="hidden lg:flex flex-col justify-center w-64 bg-studio-deep/80 border-l border-white/10 p-4">
        <h3 className="text-white font-heading text-lg mb-4 text-center">سُلم الجوائز</h3>
        <div className="space-y-1">
          {[...PRIZE_LADDER].reverse().map((prize, idx) => {
            const level = 15 - idx;
            const isActive = level === questionNumber;
            const isPassed = level < questionNumber;
            const isCheckpoint = SAFETY_CHECKPOINTS.includes(level);
            
            return (
              <div
                key={level}
                data-testid={`prize-level-${level}`}
                className={`
                  flex justify-between items-center px-3 py-1.5 rounded text-sm font-numbers
                  ${isActive ? 'bg-neon-gold/80 text-studio font-bold scale-105' : ''}
                  ${isPassed ? 'opacity-40' : ''}
                  ${isCheckpoint && !isActive ? 'text-neon-gold' : 'text-white/80'}
                  transition-all duration-300
                `}
              >
                <span>{level}</span>
                <span className={isCheckpoint ? 'font-bold' : ''}>
                  {prize.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Guaranteed Prize */}
        <div className="mt-4 p-3 bg-green-900/50 rounded-lg border border-green-500/30">
          <p className="text-green-400 text-xs mb-1">الجائزة المضمونة</p>
          <p className="text-green-300 font-numbers font-bold text-lg">
            {guaranteedPrize.toLocaleString()} ريال
          </p>
        </div>
      </div>

      {/* Main Game Area */}
      <div className="flex-1 flex flex-col p-4 lg:p-8">
        {/* Top Bar */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <span className="text-white font-body">السؤال {questionNumber} من 15</span>
            <span className="text-neon-gold font-numbers font-bold">
              {PRIZE_LADDER[questionNumber - 1]?.toLocaleString()} ريال
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              data-testid="quit-button"
              variant="ghost"
              onClick={() => setShowQuitModal(true)}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut className="ml-2 w-4 h-4" />
              انسحاب
            </Button>
            
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Prize Ladder - Mobile */}
        <div className="lg:hidden mb-4 overflow-x-auto">
          <div className="flex gap-2 pb-2">
            {PRIZE_LADDER.map((prize, idx) => {
              const level = idx + 1;
              const isActive = level === questionNumber;
              const isPassed = level < questionNumber;
              const isCheckpoint = SAFETY_CHECKPOINTS.includes(level);
              
              return (
                <div
                  key={level}
                  className={`
                    flex-shrink-0 px-3 py-1 rounded-full text-xs font-numbers
                    ${isActive ? 'bg-neon-gold text-studio font-bold' : ''}
                    ${isPassed ? 'bg-white/10 text-white/40' : 'bg-white/5 text-white/60'}
                    ${isCheckpoint && !isActive ? 'border border-neon-gold/50' : ''}
                  `}
                >
                  {prize.toLocaleString()}
                </div>
              );
            })}
          </div>
        </div>

        {/* Lifelines */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            data-testid="lifeline-5050"
            onClick={useFiftyFifty}
            disabled={!lifelines.fifty_fifty || (answerState && answerState !== 'selected')}
            className="lifeline-btn w-16 h-12 md:w-20 md:h-14 rounded-xl flex items-center justify-center"
          >
            <span className="text-white font-bold text-sm md:text-base">50:50</span>
          </button>
          
          <button
            data-testid="lifeline-audience"
            onClick={useAskAudience}
            disabled={!lifelines.ask_audience || (answerState && answerState !== 'selected')}
            className="lifeline-btn w-16 h-12 md:w-20 md:h-14 rounded-xl flex items-center justify-center"
          >
            <Users className="text-white w-5 h-5 md:w-6 md:h-6" />
          </button>
          
          <button
            data-testid="lifeline-phone"
            onClick={usePhoneFriend}
            disabled={!lifelines.phone_friend || (answerState && answerState !== 'selected')}
            className="lifeline-btn w-16 h-12 md:w-20 md:h-14 rounded-xl flex items-center justify-center"
          >
            <Phone className="text-white w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>

        {/* Question Card */}
        <div className="flex-1 flex flex-col justify-center max-w-4xl mx-auto w-full">
          <div className="question-card p-6 md:p-8 mb-8 animate-fade-in-up">
            <p className="text-white font-body text-xl md:text-2xl lg:text-3xl text-center leading-relaxed">
              {currentQuestion?.question_text}
            </p>
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['A', 'B', 'C', 'D'].map((option, idx) => (
              <button
                key={option}
                data-testid={`answer-${option}`}
                onClick={() => handleSelectAnswer(option)}
                disabled={disabledOptions.includes(option) || answerState === 'correct' || answerState === 'wrong'}
                className={`
                  ${getOptionClass(option)}
                  py-4 px-6 text-white font-body text-base md:text-lg
                  flex items-center justify-between
                  transition-all duration-300
                  animate-fade-in-up
                `}
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <span className="flex-1 text-right pr-4">{getOptionText(option)}</span>
                <span className="w-8 h-8 rounded bg-white/20 flex items-center justify-center font-numbers font-bold text-neon-gold">
                  {option}
                </span>
              </button>
            ))}
          </div>

          {/* Confirm Button */}
          {selectedAnswer && answerState === 'selected' && (
            <div className="mt-8 flex justify-center animate-fade-in-up">
              <Button
                data-testid="confirm-answer-btn"
                onClick={() => setShowConfirmModal(true)}
                disabled={isSubmitting}
                className="px-8 py-4 text-xl font-heading bg-gradient-to-r from-neon-gold to-neon-gold-dim hover:from-neon-gold-dim hover:to-neon-gold text-studio rounded-full shadow-neon-gold"
              >
                {isSubmitting ? (
                  <Loader2 className="ml-2 w-6 h-6 animate-spin" />
                ) : (
                  <CheckCircle className="ml-2 w-6 h-6" />
                )}
                تأكيد الإجابة
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Audience Poll Modal */}
      <Dialog open={showAudienceModal} onOpenChange={setShowAudienceModal}>
        <DialogContent className="bg-studio border-white/20 text-white max-w-md" aria-describedby="audience-poll-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-center text-neon-cyan">
              تصويت الجمهور
            </DialogTitle>
          </DialogHeader>
          <p id="audience-poll-description" className="sr-only">نتائج تصويت الجمهور على الإجابات</p>
          
          <div className="space-y-4 py-4">
            {audiencePoll && ['A', 'B', 'C', 'D'].map(option => (
              <div key={option} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="font-numbers">{option}</span>
                  <span className="font-numbers">{audiencePoll[option]}%</span>
                </div>
                <div className="h-6 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-neon-cyan to-neon-cyan-dim poll-bar rounded-full"
                    style={{ width: `${audiencePoll[option]}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          
          <Button
            onClick={() => setShowAudienceModal(false)}
            className="w-full bg-neon-cyan text-studio hover:bg-neon-cyan-dim"
          >
            حسناً
          </Button>
        </DialogContent>
      </Dialog>

      {/* Phone Friend Modal */}
      <Dialog open={showPhoneModal} onOpenChange={setShowPhoneModal}>
        <DialogContent className="bg-studio border-white/20 text-white max-w-md" aria-describedby="phone-friend-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-center text-neon-gold">
              الاتصال بصديق
            </DialogTitle>
          </DialogHeader>
          <p id="phone-friend-description" className="sr-only">اقتراح صديقك للإجابة الصحيحة</p>
          
          {phoneFriend && (
            <div className="py-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-neon-gold to-neon-gold-dim flex items-center justify-center">
                <Phone className="w-10 h-10 text-studio" />
              </div>
              
              <p className="font-body text-lg mb-2">
                صديقك <span className="text-neon-gold font-bold">{phoneFriend.friend_name}</span> يقول:
              </p>
              
              <p className="font-body text-xl text-white/90 my-6 leading-relaxed">
                "{phoneFriend.message}"
              </p>
              
              <div className="bg-white/5 rounded-lg p-4 mt-4">
                <p className="text-sm text-white/70 mb-1">الإجابة المقترحة</p>
                <p className="text-2xl font-numbers text-neon-gold font-bold">
                  {phoneFriend.suggested_option}
                </p>
                <p className="text-sm text-white/50 mt-1">
                  نسبة الثقة: {phoneFriend.confidence}%
                </p>
              </div>
            </div>
          )}
          
          <Button
            onClick={() => setShowPhoneModal(false)}
            className="w-full bg-neon-gold text-studio hover:bg-neon-gold-dim"
          >
            شكراً
          </Button>
        </DialogContent>
      </Dialog>

      {/* Quit Confirmation Modal */}
      <Dialog open={showQuitModal} onOpenChange={setShowQuitModal}>
        <DialogContent className="bg-studio border-white/20 text-white max-w-md" aria-describedby="quit-modal-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-center text-red-400">
              هل تريد الانسحاب؟
            </DialogTitle>
          </DialogHeader>
          <p id="quit-modal-description" className="sr-only">تأكيد الانسحاب من اللعبة</p>
          
          <div className="py-6 text-center">
            <p className="font-body text-lg mb-4">
              إذا انسحبت الآن ستربح:
            </p>
            <p className="text-4xl font-numbers text-neon-gold font-bold">
              {questionNumber > 1 ? PRIZE_LADDER[questionNumber - 2].toLocaleString() : 0} ريال
            </p>
          </div>
          
          <div className="flex gap-4">
            <Button
              data-testid="cancel-quit-btn"
              onClick={() => setShowQuitModal(false)}
              variant="outline"
              className="flex-1 border-white/30 text-white hover:bg-white/10"
            >
              استمر في اللعب
            </Button>
            <Button
              data-testid="confirm-quit-btn"
              onClick={handleQuit}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              انسحب
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Answer Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-studio border-white/20 text-white max-w-md" aria-describedby="confirm-modal-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-center text-neon-gold">
              تأكيد الإجابة النهائية
            </DialogTitle>
          </DialogHeader>
          <p id="confirm-modal-description" className="sr-only">تأكيد إجابتك النهائية</p>
          
          <div className="py-6 text-center">
            <p className="font-body text-lg mb-4">
              هل أنت متأكد من اختيارك؟
            </p>
            <div className="bg-neon-gold/20 rounded-lg p-4 border border-neon-gold/50">
              <p className="text-sm text-white/70 mb-2">إجابتك</p>
              <p className="text-2xl font-body text-white">
                <span className="font-numbers text-neon-gold">{selectedAnswer}: </span>
                {getOptionText(selectedAnswer)}
              </p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Button
              onClick={() => setShowConfirmModal(false)}
              variant="outline"
              className="flex-1 border-white/30 text-white hover:bg-white/10"
            >
              تغيير الإجابة
            </Button>
            <Button
              data-testid="final-confirm-btn"
              onClick={handleConfirmAnswer}
              className="flex-1 bg-neon-gold text-studio hover:bg-neon-gold-dim"
            >
              إجابة نهائية
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
